import React, { useEffect, useState } from 'react';
import { Link, useNavigate, type SetURLSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
    getTasks, createTask,
    type Milestone, type ProjectMember, type Task,
} from '../../../packages/api-client/pm-api';
import { getTemplates, type TaskTemplate } from '../../../packages/api-client/templates-api';
import { BulkActionBar } from '../../../components/BulkActionBar';
import { apiClient, getErrorMessage } from '../../../packages/api-client/index';
import { StateView } from '../../../packages/ui';
import { TaskFilterBar, taskFilterParams, useTaskFilters } from './taskFilters';
import { formatDueDate, parseDueDate } from '../../../utils/date';
import { StatusIcon } from '../../../components/taskIcons';
import { priorityColor } from '../../../utils/statusColors';
import { Calendar, Flag, ChevronRight, X, ArrowLeft, ArrowRight } from 'lucide-react';

// State for the Tasks section. Called unconditionally by ProjectDetailPage
// (after its initial-load effect, preserving effect order) so the list,
// selection and modal state outlive tab switches as they did inline.
export function useTasksSection(
    id: string | undefined,
    tab: string,
    searchParams: URLSearchParams,
    setSearchParams: SetURLSearchParams,
) {
    const [tasks, setTasks] = useState<Task[]>([]);
    const [tasksLoading, setTasksLoading] = useState(false);

    // ─── Tasks view state ────────────────────────────────────────────────
    // Filters (shared with the Board, see taskFilters.tsx), page and page size
    // all live in the URL: the view survives opening a task and pressing Back,
    // a reload, and a List↔Board switch, and it is shareable. A param holding
    // its default is deleted rather than written.
    const filterState = useTaskFilters(searchParams, setSearchParams);
    const { filters, update: updateTasksView } = filterState;
    // Expanded-subtask tracking for the tree view. Keyed by parent task id;
    // collapsed by default so the first render stays scannable.
    const [expandedTaskIds, setExpandedTaskIds] = useState<Set<string>>(new Set());
    // The set means "expanded" in the tree and "collapsed" in a filtered
    // (flat) list, so it starts over when that switches.
    useEffect(() => { setExpandedTaskIds(new Set()); }, [filterState.filtered]);
    // Pagination operates on *parent* tasks; subtasks come along for the
    // parents on the current page (the backend never splits them). A search or
    // milestone filter is flat, so the page then windows over matching rows.
    //
    // 100 is the ceiling: pagination.MaxLimit is 100 and a larger value does NOT
    // clamp, it silently falls back to 20. So there is no unbounded "all".
    const TASKS_PAGE_SIZES = [10, 20, 50, 100];
    const DEFAULT_TASKS_PAGE_SIZE = 20;
    const rawSize = Number(searchParams.get('size'));
    const tasksPageLimit = TASKS_PAGE_SIZES.includes(rawSize) ? rawSize : DEFAULT_TASKS_PAGE_SIZE;
    const rawPage = Number(searchParams.get('page'));
    const tasksPage = Number.isInteger(rawPage) && rawPage > 0 ? rawPage : 1;
    const [tasksTotal, setTasksTotal] = useState(0);
    // The server's page count (meta.total_pages), not one recomputed here.
    const [tasksTotalPages, setTasksTotalPages] = useState(1);
    const [tasksError, setTasksError] = useState<string | null>(null);
    const [reloadKey, setReloadKey] = useState(0);
    const setTasksPage = (p: number) => updateTasksView({ page: p <= 1 ? '' : String(p) });
    const [showAddTask, setShowAddTask] = useState(false);
    const [taskForm, setTaskForm] = useState({ title: '', description: '', priority: 'medium', due_date: '', milestone_id: '' });
    const [addingTask, setAddingTask] = useState(false);
    const [showTemplatePicker, setShowTemplatePicker] = useState(false);
    const [templates, setTemplates] = useState<TaskTemplate[]>([]);
    const [templatesLoaded, setTemplatesLoaded] = useState(false);
    const [creatingFromTemplate, setCreatingFromTemplate] = useState<string | null>(null);
    const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>([]);
    const [bulkBusy, setBulkBusy] = useState(false);
    const paramsKey = JSON.stringify(taskFilterParams(filters));

    useEffect(() => {
        if (tab !== 'Tasks' || !id) return;
        let live = true;
        setTasksLoading(true);
        setTasksError(null);
        // include_subtasks so the list renders a tree.
        const params: Record<string, string> = {
            ...JSON.parse(paramsKey) as Record<string, string>,
            page: String(tasksPage),
            limit: String(tasksPageLimit),
            include_subtasks: 'true',
        };
        getTasks(id, params).then(res => {
            if (!live) return;
            setTasks(res.data ?? []);
            setTasksTotal(res.meta?.total ?? 0);
            setTasksTotalPages(Math.max(1, res.meta?.total_pages ?? 1));
        }).catch(err => {
            if (live) setTasksError(getErrorMessage(err));
        }).finally(() => { if (live) setTasksLoading(false); });
        return () => { live = false; };
    }, [tab, id, tasksPage, tasksPageLimit, paramsKey, reloadKey]);
    const reloadTasks = () => setReloadKey(k => k + 1);

    async function markSelectedDone() {
        setBulkBusy(true);
        try {
            await apiClient.patch('/tasks/bulk', { task_ids: selectedTaskIds, status: 'done' });
            setTasks(prev => prev.map(t => selectedTaskIds.includes(t.id) ? { ...t, status: 'done' as Task['status'] } : t));
            setSelectedTaskIds([]);
        } catch (err) {
            // Nothing was changed locally; keep the selection so a retry is one click.
            toast.error(`Couldn't mark the selected tasks done: ${getErrorMessage(err)}`);
        } finally {
            setBulkBusy(false);
        }
    }

    async function handleAddTask(e: React.FormEvent) {
        e.preventDefault();
        if (!id || !taskForm.title.trim()) return;
        setAddingTask(true);
        try {
            const created = await createTask(id, {
                title: taskForm.title,
                description: taskForm.description,
                priority: taskForm.priority as Task['priority'],
                due_date: taskForm.due_date || undefined,
                milestone_id: taskForm.milestone_id || undefined,
            });
            setTasks(t => [created, ...t]);
            setTaskForm({ title: '', description: '', priority: 'medium', due_date: '', milestone_id: '' });
            setShowAddTask(false);
        } catch { /* show nothing for now */ }
        finally { setAddingTask(false); }
    }

    // Templates are lazy-loaded the first time the picker is opened so
    // projects with no template users never pay the extra request.
    useEffect(() => {
        if (!showTemplatePicker || templatesLoaded) return;
        getTemplates()
            .then(setTemplates)
            .catch(() => { /* leave list empty; the modal shows the empty state */ })
            .finally(() => setTemplatesLoaded(true));
    }, [showTemplatePicker, templatesLoaded]);

    async function handleCreateFromTemplate(t: TaskTemplate) {
        if (!id) return;
        setCreatingFromTemplate(t.id);
        try {
            const created = await createTask(id, {
                title: t.name,
                description: t.description,
                priority: t.priority as Task['priority'],
                estimated_hours: t.estimated_hours,
            });
            setTasks(prev => [created, ...prev]);
            setShowTemplatePicker(false);
        } catch { /* silent — matches handleAddTask */ }
        finally { setCreatingFromTemplate(null); }
    }

    return {
        tasks, setTasks, tasksLoading, tasksError, reloadTasks, updateTasksView, expandedTaskIds, setExpandedTaskIds,
        TASKS_PAGE_SIZES, DEFAULT_TASKS_PAGE_SIZE, tasksPageLimit, tasksPage, tasksTotal, setTasksPage,
        showAddTask, setShowAddTask, taskForm, setTaskForm, addingTask,
        showTemplatePicker, setShowTemplatePicker, templates, templatesLoaded, creatingFromTemplate,
        selectedTaskIds, setSelectedTaskIds, bulkBusy, markSelectedDone,
        filterState, tasksTotalPages,
        handleAddTask, handleCreateFromTemplate,
    };
}

export function TasksSection({ id, milestones, members, state }: {
    id: string | undefined;
    milestones: Milestone[];
    members: ProjectMember[];
    state: ReturnType<typeof useTasksSection>;
}) {
    const navigate = useNavigate();
    const {
        tasks, tasksLoading, tasksError, reloadTasks, updateTasksView, expandedTaskIds, setExpandedTaskIds,
        TASKS_PAGE_SIZES, DEFAULT_TASKS_PAGE_SIZE, tasksPageLimit, tasksPage, tasksTotal, setTasksPage,
        showAddTask, setShowAddTask, taskForm, setTaskForm, addingTask,
        showTemplatePicker, setShowTemplatePicker, templates, templatesLoaded, creatingFromTemplate,
        selectedTaskIds, setSelectedTaskIds, bulkBusy, markSelectedDone,
        filterState, tasksTotalPages,
        handleAddTask, handleCreateFromTemplate,
    } = state;
    const { filters, filtered: tasksFiltered, applyFilters } = filterState;
    const taskQuery = filters.q;
    return (
                <div>
                    <div className="tasks-toolbar">
                        <h2 className="tasks-toolbar-title">
                            Project tasks <span className="pd-count">{tasks.length}</span>
                        </h2>
                        <TaskFilterBar projectId={id!} state={filterState} milestones={milestones} members={members} />
                        <div className="task-filters-actions">
                            <button className="btn btn-ghost btn-sm" onClick={() => setShowTemplatePicker(true)}>Use template</button>
                            <button className="btn btn-primary btn-sm" onClick={() => setShowAddTask(true)}>+ Add Task</button>
                        </div>
                    </div>

                    {/* Task list */}
                    {tasksLoading ? (
                        <StateView state="loading" label="Loading tasks" lines={5} />
                    ) : tasksError ? (
                        <StateView state="error" title="Tasks couldn't be loaded" message={tasksError} onRetry={reloadTasks} />
                    ) : tasks.length === 0 && tasksFiltered ? (
                        /* Nothing matched — distinct from "no tasks yet": the
                           project may be full of work, none of it matching. */
                        <StateView state="filtered-empty"
                            title={taskQuery.trim() ? `No tasks match “${taskQuery.trim()}”` : 'No tasks match the current filters'}
                            onClearFilters={() => applyFilters({})} />
                    ) : tasks.length === 0 ? (
                        <StateView state="empty" title="No tasks yet" message="Create your first task to get started."
                            action={<>
                                <button className="btn btn-primary btn-md" onClick={() => setShowAddTask(true)}>+ Add Task</button>
                                <button className="btn btn-secondary btn-md" onClick={() => setShowTemplatePicker(true)}>Use template</button>
                            </>} />
                    ) : (() => {
                        // Group the flat list into parents + children. The
                        // backend returns both in one pass when include_subtasks
                        // is set, so we bucket them here without a second fetch.
                        const childrenByParent = new Map<string, Task[]>();
                        const parents: Task[] = [];
                        // Milestone lookup so each row can render its milestone
                        // name as a chip without a per-row search. Re-built on
                        // every render but milestones is small — fine.
                        const milestoneById = new Map(milestones.map(m => [m.id, m]));
                        for (const t of tasks) {
                            if (t.parent_id) {
                                const arr = childrenByParent.get(t.parent_id);
                                if (arr) arr.push(t); else childrenByParent.set(t.parent_id, [t]);
                            } else {
                                parents.push(t);
                            }
                        }
                        // Orphan subtasks (parent filtered out by status filter
                        // or paginated away) still deserve a home — render them
                        // as top-level so they're not silently dropped.
                        for (const t of tasks) {
                            if (t.parent_id && !parents.some(p => p.id === t.parent_id)) {
                                if (!parents.includes(t)) parents.push(t);
                            }
                        }

                        function toggleExpand(taskID: string) {
                            setExpandedTaskIds(prev => {
                                const next = new Set(prev);
                                if (next.has(taskID)) next.delete(taskID); else next.add(taskID);
                                return next;
                            });
                        }

                        const renderRow = (t: Task, depth: number, isLast: boolean) => {
                            const children = childrenByParent.get(t.id) ?? [];
                            // Filtered results are flat: every row matched, so matching
                            // subtasks start expanded under their matching parent (the set
                            // then records the ones collapsed), and only children that are
                            // in the result count — subtask_count includes unmatched ones.
                            const hasChildren = children.length > 0 || (!tasksFiltered && (t.subtask_count ?? 0) > 0);
                            const expanded = expandedTaskIds.has(t.id) !== tasksFiltered;
                            const indent = depth * 24;
                            return (
                                <React.Fragment key={t.id}>
                                    <div
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: 'var(--space-3)',
                                            padding: 'var(--space-3) var(--space-4)',
                                            paddingLeft: `calc(var(--space-4) + ${indent}px)`,
                                            borderBottom: isLast ? 'none' : '1px solid var(--color-border)',
                                            cursor: 'pointer', transition: 'background 0.15s',
                                            background: depth > 0 ? 'var(--color-surface-2)' : 'transparent',
                                        }}
                                        onClick={() => navigate(`/tasks/${t.id}`)}
                                        onMouseEnter={e => (e.currentTarget.style.background = depth > 0 ? 'var(--color-surface)' : 'var(--color-surface)')}
                                        onMouseLeave={e => (e.currentTarget.style.background = depth > 0 ? 'var(--color-surface-2)' : 'transparent')}
                                    >
                                        {/* Expand/collapse chevron — renders a spacer when there are no children so the column stays aligned. */}
                                        {hasChildren ? (
                                            <button
                                                onClick={e => { e.stopPropagation(); toggleExpand(t.id); }}
                                                aria-label={expanded ? 'Collapse subtasks' : 'Expand subtasks'}
                                                style={{
                                                    width: 18, height: 18, border: 'none', background: 'transparent',
                                                    cursor: 'pointer', color: 'var(--color-text-muted)',
                                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                    lineHeight: 1, padding: 0,
                                                    transform: expanded ? 'rotate(90deg)' : 'none',
                                                    transition: 'transform 0.15s',
                                                    flexShrink: 0,
                                                }}
                                            ><ChevronRight size={14} aria-hidden="true" /></button>
                                        ) : (
                                            <span style={{ width: 18, flexShrink: 0 }} />
                                        )}
                                        {/* Checkbox */}
                                        <input
                                            type="checkbox"
                                            aria-label={`Select ${t.title}`}
                                            checked={selectedTaskIds.includes(t.id)}
                                            onClick={e => e.stopPropagation()}
                                            onChange={e => {
                                                e.stopPropagation();
                                                setSelectedTaskIds(prev =>
                                                    e.target.checked ? [...prev, t.id] : prev.filter(x => x !== t.id)
                                                );
                                            }}
                                            style={{ flexShrink: 0, accentColor: 'var(--color-primary)' }}
                                        />
                                        <span style={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}>
                                            <StatusIcon status={t.status} size={18} />
                                        </span>
                                        {/* Priority dot */}
                                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: priorityColor(t.priority), flexShrink: 0 }} title={t.priority} />
                                        {/* Title + subtask count badge */}
                                        <span style={{ flex: 1, fontWeight: 500, textDecoration: t.status === 'done' ? 'line-through' : 'none', color: t.status === 'done' ? 'var(--color-text-muted)' : 'inherit' }}>
                                            <Link to={`/tasks/${t.id}`} className="task-row-link" onClick={e => e.stopPropagation()}>{t.title}</Link>
                                            {hasChildren && (
                                                <span style={{ marginLeft: 8, fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', fontWeight: 400 }}>
                                                    · {children.length || t.subtask_count} subtask{(children.length || t.subtask_count) === 1 ? '' : 's'}
                                                </span>
                                            )}
                                        </span>
                                        {/* Due date */}
                                        {t.due_date && (() => {
                                            const due = parseDueDate(t.due_date);
                                            const overdue = !!due && due < new Date() && t.status !== 'done';
                                            return (
                                                <span style={{ fontSize: 'var(--font-size-xs)', color: overdue ? 'var(--color-danger)' : 'var(--color-text-muted)', flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                                    <Calendar size={12} aria-hidden="true" /> {formatDueDate(t.due_date)}
                                                </span>
                                            );
                                        })()}
                                        {/* Milestone chip — visible only when the task is
                                            attached to a milestone we know about. If the
                                            milestone is missing from the lookup (e.g. it was
                                            soft-deleted and orphaned) we render nothing
                                            rather than a stale id. */}
                                        {t.milestone_id && milestoneById.get(t.milestone_id) && (
                                            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                                                title="Milestone">
                                                <Flag size={12} aria-hidden="true" /> {milestoneById.get(t.milestone_id)!.name}
                                            </span>
                                        )}
                                        {/* Status badge */}
                                        <span style={{ fontSize: 'var(--font-size-xs)', padding: '2px 8px', borderRadius: 'var(--radius-full)', background: 'var(--color-surface)', color: 'var(--color-text-muted)', flexShrink: 0, textTransform: 'capitalize' }}>
                                            {t.status.replace('_', ' ')}
                                        </span>
                                    </div>
                                    {expanded && children.map((c, ci) => renderRow(c, depth + 1, false /* parent adds the next border */))}
                                </React.Fragment>
                            );
                        };

                        return (
                            <div className="pd-panel">
                                {parents.map((t, i) => renderRow(t, 0, i === parents.length - 1))}
                            </div>
                        );
                    })()}

                    {/* Pagination — shown only when there's more than one
                        page of parents. "N parent tasks" on the label
                        because subtasks come along automatically and
                        aren't counted in the page total. */}
                    {/* Pager. Rendered whenever there are rows, not only when
                        there is more than one page: the per-page selector lives
                        here, and hiding the whole row on a single page would
                        strip away the only control that can get the user back
                        to a smaller page size. */}
                    {!tasksLoading && tasks.length > 0 && (
                        <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'center', alignItems: 'center', marginTop: 'var(--space-4)', flexWrap: 'wrap' }}>
                            {tasksTotalPages > 1 && (
                                <button className="btn btn-ghost btn-sm" aria-label="Previous page" onClick={() => setTasksPage(Math.max(1, tasksPage - 1))} disabled={tasksPage === 1}><ArrowLeft size={14} aria-hidden="true" /></button>
                            )}
                            <span style={{ padding: '0.4rem 0.75rem', color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
                                Page {tasksPage} of {tasksTotalPages} · {tasksTotal}{' '}
                                {/* Any filter flattens the list server-side, so the
                                    count is of matching tasks, not of parents. */}
                                {tasksFiltered ? 'task' : 'parent task'}{tasksTotal === 1 ? '' : 's'}
                            </span>
                            {tasksTotalPages > 1 && (
                                <button className="btn btn-ghost btn-sm" aria-label="Next page" onClick={() => setTasksPage(Math.min(tasksTotalPages, tasksPage + 1))} disabled={tasksPage === tasksTotalPages}><ArrowRight size={14} aria-hidden="true" /></button>
                            )}
                            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)', color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
                                Per page
                                <select
                                    className="form-input"
                                    style={{ padding: '4px 8px', fontSize: 'var(--font-size-sm)', width: 'auto' }}
                                    aria-label="Tasks per page"
                                    value={String(tasksPageLimit)}
                                    onChange={e => updateTasksView({
                                        size: e.target.value === String(DEFAULT_TASKS_PAGE_SIZE) ? '' : e.target.value,
                                        // Page 6 at 10-per-page is past the end
                                        // at 100-per-page; always land on 1.
                                        page: '',
                                    })}
                                >
                                    {TASKS_PAGE_SIZES.map(size => (
                                        <option key={size} value={String(size)}>{size}</option>
                                    ))}
                                </select>
                            </label>
                        </div>
                    )}

                    <BulkActionBar
                        selectedIds={selectedTaskIds}
                        busy={bulkBusy}
                        onUnselect={() => setSelectedTaskIds([])}
                        onMarkDone={markSelectedDone}
                    />

                    {/* Add Task Modal */}
                    {showAddTask && (
                        <div className="modal-overlay" onClick={() => setShowAddTask(false)}>
                            <div className="modal" onClick={e => e.stopPropagation()}>
                                <div className="modal-header">
                                    <span className="modal-title">Add Task</span>
                                    <button className="btn btn-ghost btn-sm" aria-label="Close" onClick={() => setShowAddTask(false)}><X size={16} aria-hidden="true" /></button>
                                </div>
                                <form onSubmit={handleAddTask}>
                                    <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                        <div className="form-group">
                                            <label className="form-label">Title *</label>
                                            <input className="form-input" type="text" placeholder="Task title" required autoFocus
                                                value={taskForm.title} onChange={e => setTaskForm(f => ({ ...f, title: e.target.value }))} />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Description</label>
                                            <textarea className="form-input" rows={3} placeholder="Optional description"
                                                value={taskForm.description} onChange={e => setTaskForm(f => ({ ...f, description: e.target.value }))} />
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                            <div className="form-group">
                                                <label className="form-label">Priority</label>
                                                <select className="form-input" value={taskForm.priority} onChange={e => setTaskForm(f => ({ ...f, priority: e.target.value }))}>
                                                    <option value="urgent">Urgent</option>
                                                    <option value="high">High</option>
                                                    <option value="medium">Medium</option>
                                                    <option value="low">Low</option>
                                                    <option value="none">None</option>
                                                </select>
                                            </div>
                                            <div className="form-group">
                                                <label className="form-label">Due Date</label>
                                                <input className="form-input" type="date"
                                                    value={taskForm.due_date} onChange={e => setTaskForm(f => ({ ...f, due_date: e.target.value }))} />
                                            </div>
                                        </div>
                                        {/* Milestone dropdown. Hidden when the project has no
                                            milestones to keep the modal tight — the discoverability
                                            of the milestones feature lives on the Overview tab, not
                                            here. Pops in automatically once a milestone exists. */}
                                        {milestones.length > 0 && (
                                            <div className="form-group">
                                                <label className="form-label">Milestone</label>
                                                <select className="form-input"
                                                    value={taskForm.milestone_id}
                                                    onChange={e => setTaskForm(f => ({ ...f, milestone_id: e.target.value }))}>
                                                    <option value="">— None —</option>
                                                    {milestones.map(m => (
                                                        <option key={m.id} value={m.id}>
                                                            {m.name}{m.due_date ? ` · ${formatDueDate(m.due_date)}` : ''}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}
                                    </div>
                                    <div className="modal-footer">
                                        <button className="btn btn-secondary btn-md" type="button" onClick={() => setShowAddTask(false)}>Cancel</button>
                                        <button className="btn btn-primary btn-md" type="submit" disabled={addingTask}>
                                            {addingTask ? 'Creating…' : 'Create Task'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}

                    {/* Use Template Modal — click a template to materialize
                        it as a new task in this project. Mirrors the picker
                        in TemplatesPage but skips the project-select step
                        since we already know which project we're in. */}
                    {showTemplatePicker && (
                        <div className="modal-overlay" onClick={() => !creatingFromTemplate && setShowTemplatePicker(false)}>
                            <div className="modal" onClick={e => e.stopPropagation()}>
                                <div className="modal-header">
                                    <span className="modal-title">Use template</span>
                                    <button className="btn btn-ghost btn-sm" aria-label="Close" onClick={() => setShowTemplatePicker(false)} disabled={!!creatingFromTemplate}><X size={16} aria-hidden="true" /></button>
                                </div>
                                <div className="modal-body">
                                    {!templatesLoaded ? (
                                        <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-4)' }}><div className="spinner" /></div>
                                    ) : templates.length === 0 ? (
                                        <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)', textAlign: 'center', padding: 'var(--space-3)' }}>
                                            No templates yet. Create one from the <Link to="/templates">Templates</Link> page first.
                                        </p>
                                    ) : (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', maxHeight: 360, overflowY: 'auto' }}>
                                            {templates.map(t => {
                                                const busy = creatingFromTemplate === t.id;
                                                const disabled = !!creatingFromTemplate;
                                                return (
                                                    <button
                                                        key={t.id}
                                                        disabled={disabled}
                                                        onClick={() => handleCreateFromTemplate(t)}
                                                        style={{
                                                            textAlign: 'left', padding: 'var(--space-3)',
                                                            background: 'var(--color-surface-2)', border: '1px solid var(--color-border)',
                                                            borderRadius: 'var(--radius-md)', cursor: disabled ? 'wait' : 'pointer',
                                                            color: 'var(--color-text)',
                                                        }}
                                                    >
                                                        <div style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>
                                                            {t.name}{busy && <span style={{ marginLeft: 8, color: 'var(--color-text-muted)', fontWeight: 400 }}>Creating…</span>}
                                                        </div>
                                                        {t.description && (
                                                            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 2 }}>{t.description}</div>
                                                        )}
                                                        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-faint)', marginTop: 4 }}>
                                                            {t.priority !== 'none' && <span style={{ marginRight: 8 }}>Priority: {t.priority}</span>}
                                                            {t.estimated_hours && <span>{t.estimated_hours}h est.</span>}
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                                <div className="modal-footer">
                                    <button className="btn btn-secondary btn-md" type="button" onClick={() => setShowTemplatePicker(false)} disabled={!!creatingFromTemplate}>Cancel</button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
    );
}
